/* eslint-disable no-magic-numbers */
import React, { useLayoutEffect, useRef, useState } from 'react';
import * as styles from './arrow-scroll.scss';
import Icon from 'carbon-react/lib/components/icon';
import { useIntl } from 'react-intl';

const SCROLL_STEP = 320;

interface Props {
  children: JSX.Element;
}
export const ArrowScroll = ({ children }: Props) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftControlActive, setLeftControlActive] = useState(false);
  const [rightControlActive, setRightControlActive] = useState(false);

  useLayoutEffect(() => {
    let isMounted = true;
    const element = containerRef.current;

    const updateActiveControllers = () => {
      if (element && isMounted) {
        const parentWidth = element.clientWidth;
        const insideWidth = element.getElementsByClassName('innerContent')[0]?.clientWidth;
        const diff = insideWidth - parentWidth;
        if (diff > 0) {
          setLeftControlActive(!!element.scrollLeft);
          setRightControlActive(diff - element.scrollLeft > 2);
        } else {
          setLeftControlActive(false);
          setRightControlActive(false);
        }
      }
    };

    setTimeout(() => updateActiveControllers(), 100);
    window.addEventListener('resize', updateActiveControllers);
    if (element) {
      element.addEventListener('scroll', updateActiveControllers);
    }

    return () => {
      isMounted = false;
      window.removeEventListener('resize', updateActiveControllers);
      if (element) {
        element.removeEventListener('scroll', updateActiveControllers);
      }
    };
  }, [children]);

  const scrollLeft = () => {
    const newPos = (containerRef.current?.scrollLeft || SCROLL_STEP) - SCROLL_STEP;
    containerRef.current?.scrollTo({ left: newPos < 0 ? 0 : newPos, behavior: 'smooth' });
  };

  const scrollRight = () => {
    const newPos = (containerRef.current?.scrollLeft || 0) + SCROLL_STEP;
    containerRef.current?.scrollTo({ left: newPos, behavior: 'smooth' });
  };

  return (
    <>
      {(leftControlActive || rightControlActive) && (
        <div className={styles.scrollControls}>
          <button
            className={!leftControlActive ? styles.disabled : ''}
            onClick={scrollLeft}
            aria-label={intl.formatMessage({
              id: 'common.scroll_left.aria_label',
              defaultMessage: 'Scroll left button'
            })}
          >
            <Icon type="chevron_left" />
          </button>
          <button
            className={!rightControlActive ? styles.disabled : ''}
            onClick={scrollRight}
            aria-label={intl.formatMessage({
              id: 'common.scroll_right.aria_label',
              defaultMessage: 'Scroll right button'
            })}
          >
            <Icon type="chevron_right" />
          </button>
        </div>
      )}
      <div className={styles.arrowScrollContainer} ref={containerRef} tabIndex={0}>
        <div className="innerContent" style={{ width: 'max-content' }}>
          {children}
        </div>
      </div>
    </>
  );
};
